<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import type { OfferBalance, OfferMedium } from '~/types/Catalog';

const props = defineProps<{ offer: OfferMedium; recommendationsBlockId?: string }>();

const cartStore = useCartStore();
const route = useRoute();

const currentOffer = computed(() => props.offer);

const offerForDataLayer = computed(() => ({
  ...currentOffer.value,
  brand: props.offer.brand,
  displayable_offer_properties: props.offer.displayable_offer_properties,
  price: props.offer.price,
}));

const countInCart = computed(() => {
  const currentOfferInCart = cartStore.countedCartItems.find((item) => item.id === currentOffer.value.id);

  return currentOfferInCart?.count ?? 0;
});

const currentOfferAvailableCount = computed(() => {
  let count = 0;

  currentOffer.value.offer_balances.forEach((balance: OfferBalance) => {
    if (balance.warehouse.delivery_available) count += balance.balance;
  });

  return count;
});

const isCurrentOfferOutOfStock = computed(() => {
  return (
    currentOffer.value.offer_balances.length > 0 &&
    currentOffer.value.offer_balances.every((balance: OfferBalance) => balance.balance < 1)
  );
});

function checkIsAvailableOnlyOffline(offer: OfferMedium) {
  let count = 0;

  offer.offer_balances.forEach((balance: OfferBalance) => {
    if (balance.warehouse.delivery_available) count += balance.balance;
  });

  return count < 1 && offer.offer_balances.some((balance: OfferBalance) => balance.balance > 0);
}

const isNotAvailableToIncrement = computed(() => countInCart.value >= currentOfferAvailableCount.value);

const isLoading = ref('');
const { sendOfferEventToDataLayer } = useGtmModule();

async function addToCart() {
  if (isCurrentOfferOutOfStock.value) return;

  if (countInCart.value) return navigateTo(props.offer.uri);

  const showNotAvailableToast = () => {
    const toast = useToast();

    toast.error({
      component: ToastComponent,
      props: { text: 'Товар доступен только для покупки в розничном магазине' },
    });
  };

  if (checkIsAvailableOnlyOffline(currentOffer.value)) {
    showNotAvailableToast();
    return;
  }

  if (isLoading.value) return;

  isLoading.value = 'increment';

  const isAddedToCart = await cartStore.addToCart(currentOffer.value.id);

  if (isAddedToCart) {
    await sendOfferEventToDataLayer('add_to_cart', offerForDataLayer.value as unknown as OfferMedium);
    window.roistat?.event?.send('add_to_cart');
    if (window.r46) {
      const blockId = props.recommendationsBlockId ?? route.query.recommended_code;
      const params = blockId
        ? { id: currentOffer.value.id, recommended_by: 'dynamic', recommended_code: blockId, amount: 1 }
        : { id: currentOffer.value.id, amount: 1 };

      window.r46('track', 'cart', params);
    }
  }

  isLoading.value = '';
}

async function incrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'increment';

  const isSuccess = await cartStore.increment(currentOffer.value.id);

  if (isSuccess) {
    await sendOfferEventToDataLayer('add_to_cart', offerForDataLayer.value as unknown as OfferMedium);
    if (window.r46) {
      const item = cartStore.countedCartItems.find((i) => i.id === currentOffer.value.id);
      const blockId = props.recommendationsBlockId ?? route.query.recommended_code;
      const params = blockId
        ? { id: currentOffer.value.id, recommended_by: 'dynamic', recommended_code: blockId, amount: item?.count }
        : { id: currentOffer.value.id, amount: item?.count };

      window.r46('track', 'cart', params);
    }
  }

  isLoading.value = '';
}

async function decrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'decrement';

  const isSuccess = await cartStore.decrement(currentOffer.value.id);

  if (isSuccess) {
    if (countInCart.value === 0) {
      await sendOfferEventToDataLayer('remove_from_cart', offerForDataLayer.value as unknown as OfferMedium);
      window.roistat?.event?.send('remove_from_cart');
    }
  }

  isLoading.value = '';
}
</script>

<template>
  <component
    :is="countInCart || isCurrentOfferOutOfStock ? 'div' : 'button'"
    :class="{
      'product-card-footer--in-cart': countInCart,
      'product-card-footer--out-of-stock': isCurrentOfferOutOfStock,
    }"
    :disabled="Boolean(isLoading) || isCurrentOfferOutOfStock"
    class="product-card-footer"
    @click.stop.prevent="!isCurrentOfferOutOfStock && addToCart()"
  >
    <div class="flex flex-col items-start gap-1">
      <div class="font-bold">{{ offer.price.toLocaleString('ru-RU') }}&nbsp;₽</div>

      <div
        v-if="offer.old_price"
        :class="{ '!text-black-40': countInCart }"
        class="line-through text-black-60 text-xs sm:text-sm !leading-none"
      >
        {{ offer.old_price.toLocaleString('ru-RU') }}&nbsp;₽
      </div>
    </div>

    <div class="flex justify-center items-center">
      <Transition
        v-if="countInCart === 0 && Boolean(isLoading)"
        class="w-5 ml-1"
        name="loader"
      >
        <SvgoSpinner
          class="animate-spin"
          title="Идет загрузка..."
        />
      </Transition>

      <template v-if="countInCart">
        <ProductCardCounterBlock
          :count-in-cart="countInCart"
          :is-loading="isLoading"
          :is-not-available-to-increment="isNotAvailableToIncrement"
          @decrement="decrementCountInCart"
          @increment="incrementCountInCart"
        />
      </template>

      <div
        v-if="isCurrentOfferOutOfStock"
        class="product-card-footer__out-of-stock"
      >
        Нет в наличии
      </div>

      <div
        v-else
        class="product-card-footer__add-to-cart-icon"
      >
        <SvgoCart
          class="text-xl"
          filled
        />
      </div>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.product-card-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  justify-content: space-between;
  align-items: center;
  padding: 4px 6px;
  border: 1px solid $black-10;
  border-radius: 8px;
  font-size: $text-sm;
  transition: all 0.3s;

  @include screen-xs {
    gap: 8px;
    min-height: 40px;
  }

  @include screen-sm {
    gap: 16px;
  }

  @include screen-md {
    font-size: $text-base;
  }

  &--in-cart {
    border-color: $accent !important;
    background-color: $accent !important;
    color: $white;
  }

  &--out-of-stock {
    color: $black-40;
    cursor: default;
  }

  &__add-to-cart-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    background-color: transparent;
  }
  &__out-of-stock {
    color: $black-40;
    font-size: $text-xs;
  }

  &__offer-list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 44px;
    padding: 1rem 0;
    border-bottom: 1px solid $black-20;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $black-60;
    }

    &--disabled {
      color: $black-40;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
